import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnOrderState,
  type ExpandedState,
  type GroupingState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { personName, userDetailResponseSchema, type OrgRosterRow, type Unit } from '@unit/shared';
import { apiPatch } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import type { BulkPick } from '../issuance/BulkIssuePage.js';
import { AddUnitForm, UnitAdminRow } from './OrgAdminControls.js';

/**
 * The org structure as one grid (owner, 2026-09-13: "the table is already
 * the org structure"). Every soldier in reach, grouped platoon → team under
 * a collapsible, sticky header row per unit that carries the unit's counts
 * and its actions: open the roster, issue to the unit, and for an admin
 * rename / (de)activate / add a team. Units with nobody in reach still get
 * a header row, so the structure is complete for everyone and an admin can
 * edit a team that is still empty.
 *
 * Sorting, per-column and quick filters, row selection (a unit's checkbox
 * selects its soldiers), column reorder by drag, column visibility, column
 * resize; the arrangement is remembered per browser.
 *
 * TanStack Table (MIT), not AG Grid: nesting — tree data and row grouping —
 * is an AG Grid *Enterprise* feature (checked against the v36 docs on
 * 2026-09-13). TanStack is headless, so this file also owns the markup —
 * which is what makes the sticky group rows and RTL plain CSS.
 */

/** A unit with nobody in reach is still a row, so its group header exists. */
const GHOST = 'ghost:';
const isGhost = (r: OrgRosterRow) => r.uid.startsWith(GHOST);

export function OrgGrid({
  rows,
  units,
  viewableUnitIds,
  admin,
  canIssue,
  storeroom,
  onChanged,
}: {
  rows: OrgRosterRow[];
  units: Unit[];
  viewableUnitIds: string[];
  admin: boolean;
  canIssue: boolean;
  storeroom: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [grouped, setGrouped] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => remembered().order);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => remembered().visibility);
  const [columnsMenu, setColumnsMenu] = useState(false);
  const [targetTeam, setTargetTeam] = useState('');
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [headHeight, setHeadHeight] = useState(0);
  const move = useAction(async (uids: string[], unitId: string) => {
    for (const uid of uids) await apiPatch(`/users/${uid}`, userDetailResponseSchema, { unitId });
    return true;
  });

  useEffect(() => remember({ order: columnOrder, visibility: columnVisibility }), [columnOrder, columnVisibility]);
  // The group rows stick below the header, so they need its height.
  useLayoutEffect(() => {
    const el = theadRef.current;
    if (!el) return;
    const measure = () => setHeadHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const selectable = admin || canIssue;
  const viewable = useMemo(() => new Set(viewableUnitIds), [viewableUnitIds]);
  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);
  // The name alone (owner, 2026-09-19): the rank has its own column, so a
  // search or sort on the name column is a search on the name.
  const fullName = personName;

  // One ghost row per unit with no soldier in reach: the structure stays
  // whole, and a freshly created team has a row to be edited on.
  const data = useMemo<OrgRosterRow[]>(() => {
    const seen = new Set<string>();
    for (const r of rows) {
      if (r.platoonId) seen.add(r.platoonId);
      if (r.teamId) seen.add(r.teamId);
    }
    const ghosts: OrgRosterRow[] = [];
    for (const u of units) {
      if (seen.has(u.id)) continue;
      const platoon = u.type === 'platoon' ? u : unitById.get(u.parentId ?? '');
      ghosts.push({
        uid: `${GHOST}${u.id}`,
        personalNumber: '',
        firstName: '',
        lastName: '',
        rank: null,
        title: null,
        roles: [],
        teamId: u.type === 'team' ? u.id : null,
        platoonId: platoon?.id ?? null,
        status: 'active',
        platoonName: platoon?.name ?? null,
        teamName: u.type === 'team' ? u.name : null,
        openGear: 0,
        unsignedForms: 0,
        overdue: 0,
      });
    }
    return [...rows, ...ghosts];
  }, [rows, units, unitById]);

  const columns = useMemo<ColumnDef<OrgRosterRow>[]>(() => {
    const sum = (key: 'openGear' | 'unsignedForms' | 'overdue'): ColumnDef<OrgRosterRow>['aggregationFn'] => (_col, leaf) =>
      leaf.reduce((n, r) => n + r.original[key], 0);
    const num = (id: 'openGear' | 'unsignedForms' | 'overdue', header: string, warn = false): ColumnDef<OrgRosterRow> => ({
      id,
      accessorKey: id,
      header,
      aggregationFn: sum(id),
      enableColumnFilter: false,
      size: 96,
      meta: { align: 'end' },
      cell: (c) => <Num value={c.getValue<number>()} warn={warn} />,
      aggregatedCell: (c) => <Num value={c.getValue<number>()} warn={warn} muted />,
    });
    // Groups sort by unit order whatever the sort column says; soldiers by the column.
    const unitOrder = (r: Row<OrgRosterRow>) => {
      const u = unitById.get(String(r.groupingValue ?? ''));
      if (u) return u.type === 'platoon' ? u.order : (unitById.get(u.parentId ?? '')?.order ?? 0) * 1000 + u.order;
      // The "directly on the platoon" group (no team, story 32): first under its
      // platoon. Sorting runs before the row model's links exist, so read the
      // platoon off the group's own sub-rows rather than through the row API.
      const platoon = unitById.get(r.subRows[0]?.original.platoonId ?? '');
      return platoon ? platoon.order * 1000 : Number.MAX_SAFE_INTEGER;
    };
    const groupsFirst = (cmp: (a: Row<OrgRosterRow>, b: Row<OrgRosterRow>) => number) => (a: Row<OrgRosterRow>, b: Row<OrgRosterRow>) =>
      a.getIsGrouped() || b.getIsGrouped() ? unitOrder(a) - unitOrder(b) : cmp(a, b);
    return [
      {
        id: 'name',
        accessorFn: fullName,
        header: t('org.columns.name'),
        size: 220,
        aggregatedCell: () => null,
        sortingFn: groupsFirst((a, b) => fullName(a.original).localeCompare(fullName(b.original), 'he')),
      },
      {
        id: 'platoon',
        accessorFn: (r) => r.platoonName ?? '',
        header: t('org.type.platoon'),
        size: 120,
        // Grouped by id, shown by name: two units may share a name.
        getGroupingValue: (r) => r.platoonId ?? '',
        aggregatedCell: () => null,
        sortingFn: groupsFirst((a, b) => (a.original.platoonName ?? '').localeCompare(b.original.platoonName ?? '', 'he')),
      },
      {
        id: 'team',
        accessorFn: (r) => r.teamName ?? '',
        header: t('org.type.team'),
        size: 120,
        getGroupingValue: (r) => r.teamId ?? '',
        aggregatedCell: () => null,
        sortingFn: groupsFirst((a, b) => (a.original.teamName ?? '').localeCompare(b.original.teamName ?? '', 'he')),
      },
      { id: 'rank', accessorFn: (r) => r.rank ?? '', header: t('users.rank'), size: 100, aggregatedCell: () => null },
      { id: 'personalNumber', accessorKey: 'personalNumber', header: t('org.columns.personalNumber'), size: 130, meta: { latin: true }, aggregatedCell: () => null },
      { id: 'title', accessorFn: (r) => r.title ?? '', header: t('org.columns.title'), size: 140, aggregatedCell: () => null },
      {
        id: 'roles',
        accessorFn: (r) => r.roles.map((x) => t(`roles.${x}`)).join(' · '),
        header: t('org.columns.roles'),
        size: 160,
        aggregatedCell: () => null,
      },
      num('openGear', t('org.columns.gear')),
      num('unsignedForms', t('org.columns.unsigned')),
      num('overdue', t('org.columns.overdue'), true),
      { id: 'status', accessorFn: (r) => (isGhost(r) ? '' : t(`status.${r.status}`)), header: t('users.status'), size: 100, aggregatedCell: () => null },
    ];
  }, [t, unitById]);

  const grouping = useMemo<GroupingState>(() => (grouped ? ['platoon', 'team'] : []), [grouped]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, expanded, rowSelection, globalFilter, grouping, columnOrder, columnVisibility },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: (r) => r.uid,
    enableRowSelection: (row) => selectable && !isGhost(row.original),
    enableSubRowSelection: selectable,
    columnResizeMode: 'onChange',
    // A group row must not vanish because its own cells do not match: the
    // filters run on the leaves, then the group is rebuilt from what passed.
    filterFromLeafRows: true,
    groupedColumnMode: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false,
  });

  const selected = table.getSelectedRowModel().flatRows.filter((r) => !r.getIsGrouped() && !isGhost(r.original)).map((r) => r.original);
  const leafCount = table.getFilteredRowModel().flatRows.filter((r) => !r.getIsGrouped() && !isGhost(r.original)).length;
  const teams = units.filter((u) => u.type === 'team' && u.active);
  const platoonName = (id: string | null) => unitById.get(id ?? '')?.name ?? '';

  // Column reorder by dragging a header onto another.
  const onDrop = useCallback(
    (e: DragEvent, targetId: string) => {
      e.preventDefault();
      const sourceId = e.dataTransfer.getData('text/column');
      if (!sourceId || sourceId === targetId) return;
      const order = table.getAllLeafColumns().map((c) => c.id);
      const from = order.indexOf(sourceId);
      const to = order.indexOf(targetId);
      order.splice(from, 1);
      order.splice(to, 0, sourceId);
      setColumnOrder(order);
    },
    [table],
  );

  const resetArrangement = () => {
    setColumnOrder([]);
    setColumnVisibility({});
  };

  const visibleColumnCount = table.getVisibleLeafColumns().length + (selectable ? 1 : 0);
  const rowHeight = 36;

  return (
    <div data-testid="org-grid">
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <input
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('org.search')}
          data-testid="org-search"
          className="min-w-48 grow rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
        />
        <span className="text-xs text-slate-500" data-testid="org-grid-count">
          {t('org.soldierCount', { count: leafCount })}
        </span>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} data-testid="org-grid-grouped" />
          {t('org.groupByUnit')}
        </label>
        {grouped && (
          <span className="flex gap-2 text-xs">
            <button type="button" onClick={() => table.toggleAllRowsExpanded(true)} className="text-slate-500 underline-offset-4 hover:underline" data-testid="org-grid-expand-all">{t('org.expandAll')}</button>
            <button type="button" onClick={() => table.toggleAllRowsExpanded(false)} className="text-slate-500 underline-offset-4 hover:underline" data-testid="org-grid-collapse-all">{t('org.collapseAll')}</button>
          </span>
        )}
        <span className="relative">
          <button type="button" onClick={() => setColumnsMenu((v) => !v)} className="text-xs text-slate-500 underline-offset-4 hover:underline" data-testid="org-grid-columns">
            {t('org.columnsMenu')}
          </button>
          {columnsMenu && (
            <div className="absolute end-0 z-30 mt-1 w-56 rounded-xl bg-white p-2 text-sm shadow-lg ring-1 ring-slate-200" data-testid="org-grid-columns-menu">
              {table.getAllLeafColumns().map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-1 py-0.5 text-slate-700">
                  <input type="checkbox" checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} disabled={c.id === 'name'} />
                  {String(c.columnDef.header)}
                </label>
              ))}
              <button type="button" onClick={resetArrangement} className="mt-1 w-full rounded-lg px-1 py-1 text-start text-xs text-slate-500 hover:bg-slate-100">
                {t('org.resetColumns')}
              </button>
            </div>
          )}
        </span>
        {admin && <AddUnitForm parent={null} onCreated={onChanged} />}
      </div>

      {/* The actions float over the page instead of sitting above the table:
          an inline bar appearing on the first tick pushes the whole grid down,
          and the row the user was about to click moves under the pointer. */}
      {selected.length > 0 && (canIssue || admin) && (
        <div
          className="fixed bottom-4 inset-x-4 z-30 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-white p-3 shadow-lg ring-1 ring-slate-300 sm:inset-x-auto sm:end-6"
          role="toolbar"
          aria-label={t('org.selectedCount', { count: selected.length })}
          data-testid="org-selection-actions"
        >
          <span className="text-sm font-medium text-slate-900">{t('org.selectedCount', { count: selected.length })}</span>
          {canIssue && (
            <span className="flex flex-wrap items-center gap-2" data-testid="org-issue-selected">
              {selected.some((r) => r.status !== 'active') ? (
                <span className="text-xs text-amber-700" data-testid="org-issue-inactive">{t('org.issueSelectedInactive')}</span>
              ) : (
                <button
                  type="button"
                  // The pick travels as router state, not a query string: a
                  // 200-uid URL is nothing to bookmark. A refresh lands on the
                  // ordinary target picker.
                  onClick={() => navigate('/issue/bulk', { state: { soldierUids: selected.map((r) => r.uid) } satisfies BulkPick })}
                  className="rounded-lg bg-slate-900 px-3 py-1 text-sm font-medium text-white transition hover:bg-slate-700"
                  data-testid="org-issue-selected-go"
                >
                  {t('org.issueSelected', { count: selected.length })}
                </button>
              )}
            </span>
          )}
          {admin && (
            <span className="flex flex-wrap items-center gap-2" data-testid="org-move">
              <span className="text-sm text-slate-700">{t('org.moveSelected', { count: selected.length })}</span>
              <select
                value={targetTeam}
                onChange={(e) => setTargetTeam(e.target.value)}
                data-testid="org-move-team"
                className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
              >
                <option value="">—</option>
                {teams.map((u) => (
                  <option key={u.id} value={u.id}>
                    {platoonName(u.parentId)} · {u.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!targetTeam || move.busy}
                onClick={() =>
                  void move.run(selected.map((r) => r.uid), targetTeam).then((ok) => {
                    if (ok) {
                      setRowSelection({});
                      onChanged();
                    }
                  })
                }
                data-testid="org-move-submit"
                className="rounded-lg bg-slate-900 px-3 py-1 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
              >
                {t('users.move')}
              </button>
              <ActionError error={move.error} />
            </span>
          )}
          <button
            type="button"
            onClick={() => setRowSelection({})}
            className="text-xs text-slate-500 underline-offset-4 hover:underline"
            data-testid="org-selection-clear"
          >
            {t('org.clearSelection')}
          </button>
        </div>
      )}

      {/* Height-capped (sticky unit rows, one scroll box) on desktop only; on a phone the page scrolls. */}
      <div className="overflow-auto rounded-xl ring-1 ring-slate-200 lg:max-h-[min(72vh,900px)]">
        <table className="w-full border-separate border-spacing-0 text-sm [&_td]:border-b [&_td]:border-e [&_td]:border-slate-200 [&_td:last-child]:border-e-0 [&_th]:border-b [&_th]:border-e [&_th]:border-slate-200 [&_th:last-child]:border-e-0" style={{ minWidth: table.getTotalSize() + (selectable ? 40 : 0) }} data-testid="org-table">
          <thead ref={theadRef} className="sticky top-0 z-20 bg-slate-50 text-xs text-slate-600">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {selectable && (
                  <th className="w-10 px-2 py-2 align-top">
                    <input
                      type="checkbox"
                      aria-label={t('org.selectAll')}
                      checked={table.getIsAllRowsSelected()}
                      ref={(el) => {
                        if (el) el.indeterminate = table.getIsSomeRowsSelected();
                      }}
                      onChange={table.getToggleAllRowsSelectedHandler()}
                      data-testid="org-select-all"
                    />
                  </th>
                )}
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    style={{ width: h.getSize() }}
                    className="relative px-2 py-2 text-start align-top font-medium select-none"
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/column', h.column.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(e, h.column.id)}
                    data-testid={`org-col-${h.column.id}`}
                  >
                    <button type="button" onClick={h.column.getToggleSortingHandler()} className="flex w-full items-center gap-1 text-start hover:text-slate-900">
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      <span className="text-[10px] text-slate-400">{{ asc: '▲', desc: '▼' }[h.column.getIsSorted() as string] ?? ''}</span>
                    </button>
                    {h.column.getCanFilter() ? (
                      <input
                        value={(h.column.getFilterValue() as string) ?? ''}
                        onChange={(e) => h.column.setFilterValue(e.target.value)}
                        placeholder={t('org.filterPlaceholder')}
                        className="mt-1 w-full rounded border border-slate-300 px-1.5 py-0.5 text-xs font-normal focus:border-slate-500 focus:outline-none"
                        data-testid={`org-filter-${h.column.id}`}
                      />
                    ) : (
                      <span className="mt-1 block h-[22px]" />
                    )}
                    <span
                      onMouseDown={h.getResizeHandler()}
                      onTouchStart={h.getResizeHandler()}
                      className="absolute inset-y-0 end-0 w-1.5 cursor-col-resize hover:bg-slate-300"
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white">
            {table.getRowModel().rows.map((row) => {
              if (row.getIsGrouped()) {
                const unit = unitById.get(String(row.groupingValue ?? ''));
                // getLeafRows() includes the nested group rows themselves; only real soldiers count.
                const leaves = row.getLeafRows().filter((r) => !r.getIsGrouped() && !isGhost(r.original)).length;
                // A platoon's ghost row has no team and would show an empty
                // "directly on the platoon" group; only real soldiers earn one.
                if (!unit && leaves === 0) return null;
                const canOpen = !!unit && viewable.has(unit.id);
                // Platoon rows stick under the header; team rows under the platoon row.
                const top = headHeight + (row.depth === 1 ? rowHeight : 0);
                const cellCls = `sticky px-2 py-1.5 ${row.depth === 0 ? 'bg-slate-100 z-[12]' : 'bg-slate-50 z-[11]'} ${unit && !unit.active ? 'opacity-60' : ''}`;
                return (
                  <tr key={row.id} data-testid={unit ? `unit-${unit.id}` : `org-group-${row.id}`} data-depth={row.depth} aria-expanded={row.getIsExpanded()}>
                    {selectable && (
                      <td className={cellCls} style={{ top, height: rowHeight }}>
                        {leaves > 0 && (
                          // The unit's box reflects its soldiers: selection state only ever
                          // holds soldier rows (select-all included), and the library's own
                          // sub-row check would count a nested team row as "not selected".
                          <input
                            type="checkbox"
                            checked={leafSelection(row) === 'all'}
                            ref={(el) => {
                              if (el) el.indeterminate = leafSelection(row) === 'some';
                            }}
                            onChange={() => {
                              if (leafSelection(row) !== 'all') {
                                row.toggleSelected(true);
                                return;
                              }
                              // toggleSelected(false) is a no-op on a group row that was
                              // never itself "selected"; drop its soldiers by hand.
                              const leafIds = new Set(row.getLeafRows().filter((r) => !r.getIsGrouped()).map((r) => r.id));
                              setRowSelection((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !leafIds.has(id))));
                            }}
                            aria-label={unit?.name ?? ''}
                          />
                        )}
                      </td>
                    )}
                    <td className={cellCls} style={{ top, height: rowHeight }} colSpan={visibleColumnCount - (selectable ? 1 : 0)}>
                      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${row.depth === 1 ? 'ps-5' : ''}`}>
                        <button
                          type="button"
                          onClick={() => row.toggleExpanded()}
                          className={`flex items-center gap-2 text-start ${row.depth === 0 ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}
                          data-testid={unit ? `org-group-toggle-${unit.id}` : undefined}
                        >
                          <span className="inline-block w-3 text-xs text-slate-400">{row.getIsExpanded() ? '▾' : '▸'}</span>
                          <span>{unit?.name ?? t('org.directGroup')}</span>
                        </button>
                        <span className="text-xs text-slate-500">
                          {unit && t(`org.type.${unit.type}`)}
                          {unit && !unit.active && <span className="ms-1 rounded bg-slate-200 px-1.5 py-0.5">{t('org.inactive')}</span>}
                        </span>
                        {canOpen ? (
                          <span className="latin-inline text-xs text-slate-600" data-testid={unit ? `org-count-${unit.id}` : undefined}>
                            {t('org.soldierCount', { count: leaves })}
                            <Aggregates row={row} />
                          </span>
                        ) : unit ? (
                          <span className="text-xs text-slate-400" title={t('org.notYours')}>{t('org.notYours')}</span>
                        ) : (
                          <span className="latin-inline text-xs text-slate-600">{t('org.soldierCount', { count: leaves })}</span>
                        )}
                        {canOpen && unit && (
                          <Link to={`/org/${unit.id}`} className="text-xs text-slate-500 underline-offset-4 hover:underline" data-testid={`org-open-${unit.id}`} onClick={(e) => e.stopPropagation()}>
                            {t('org.openRoster')} ↗
                          </Link>
                        )}
                        {canOpen && unit && unit.active && canIssue && leaves > 0 && (
                          <Link to={`/issue/bulk?unit=${unit.id}`} className="text-xs text-slate-500 underline-offset-4 hover:underline" data-testid={`org-bulk-${unit.id}`}>
                            {t('bulk.button')}
                          </Link>
                        )}
                        {admin && unit && (
                          <span className="text-xs text-slate-500">
                            <UnitAdminRow unit={unit} onChanged={onChanged} />
                          </span>
                        )}
                        {admin && unit && unit.type === 'platoon' && unit.active && <AddUnitForm parent={unit} onCreated={onChanged} />}
                      </div>
                    </td>
                  </tr>
                );
              }
              if (isGhost(row.original)) return null;
              return (
                <tr
                  key={row.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/org/${row.original.teamId ?? row.original.platoonId ?? '_'}/soldier/${row.original.uid}`)}
                  data-testid={`org-row-${row.original.uid}`}
                  data-depth={row.depth}
                >
                  {selectable && (
                    <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                        aria-label={fullName(row.original)}
                      />
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as { align?: 'end'; latin?: boolean } | undefined;
                    return (
                      // The isolation goes on a span *inside* the cell, never on the
                      // cell: `direction: ltr` on the td swaps which side its
                      // `border-e` is on, and the line between this column and the
                      // next disappears in the RTL table.
                      <td key={cell.id} className={`px-2 py-1.5 ${meta?.align === 'end' ? 'text-end' : ''}`}>
                        {meta?.latin ? (
                          <span className="latin-inline">{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {storeroom && (
        <div className="mt-2 text-end">
          <Link to="/issue/bulk?unit=company" className="text-xs text-slate-500 underline-offset-4 hover:underline" data-testid="org-bulk-company">
            {t('bulk.button')} · {t('bulk.company')}
          </Link>
        </div>
      )}
    </div>
  );
}

/** How much of a unit is selected, counted over its selectable soldiers only. */
function leafSelection(row: Row<OrgRosterRow>): 'all' | 'some' | 'none' {
  const leaves = row.getLeafRows().filter((r) => !r.getIsGrouped() && r.getCanSelect());
  const n = leaves.filter((r) => r.getIsSelected()).length;
  return n === 0 ? 'none' : n === leaves.length ? 'all' : 'some';
}

/** The unit's sums, on its header: open gear, unsigned forms, overdue — the latter two only when non-zero. */
function Aggregates({ row }: { row: Row<OrgRosterRow> }) {
  const sum = (key: 'openGear' | 'unsignedForms' | 'overdue') => row.getLeafRows().reduce((n, r) => n + (r.getIsGrouped() ? 0 : r.original[key]), 0);
  const unsigned = sum('unsignedForms');
  const overdue = sum('overdue');
  return (
    <>
      <span className="ms-2 text-slate-400">·</span> {sum('openGear')} ⚙
      {unsigned > 0 && <span className="ms-2 text-amber-700">✎{unsigned}</span>}
      {overdue > 0 && <span className="ms-2 text-red-700">!{overdue}</span>}
    </>
  );
}

function Num({ value, warn = false, muted = false }: { value: number; warn?: boolean; muted?: boolean }) {
  return (
    <span className={`latin-inline ${warn && value > 0 ? 'text-red-600' : muted ? 'text-slate-500' : 'text-slate-700'}`}>{value}</span>
  );
}

// ---------------------------------------------------------------------------
// Remembered column arrangement — per browser, a convenience, never the truth.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'unit:org-grid:columns';
interface Remembered {
  order: ColumnOrderState;
  visibility: VisibilityState;
}

function remembered(): Remembered {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const v = JSON.parse(raw) as Partial<Remembered>;
      return { order: Array.isArray(v.order) ? v.order : [], visibility: v.visibility && typeof v.visibility === 'object' ? v.visibility : {} };
    }
  } catch {
    /* storage unavailable: defaults */
  }
  return { order: [], visibility: {} };
}

function remember(v: Remembered): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}
